import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ChefProfileForm from '../components/ChefProfileForm';
import ChefMediaGallery from '../components/ChefMediaGallery';
import ReceiptUploadPanel from '../components/ReceiptUploadPanel';
import ChefSubscription from '../components/ChefSubscription';
import ChatWidget from '../components/ChatWidget';
import VideoCallModal from '../components/VideoCallModal';
import ChefStatusControl from '../components/ChefStatusControl';
import NotificationBanner from '../components/NotificationBanner';
import ChefCateringManagement from '../components/ChefCateringManagement';

const ChefDashboard = ({ user, token }) => {
  const [bookings, setBookings] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showVideoCall, setShowVideoCall] = useState(false);

  useEffect(() => {
    fetchBookings();
    fetchProfile();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get('/booking');
      setBookings(response.data);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`/chef/profile/${user.id}`);
      setProfile(response.data);
    } catch (err) {
      console.log('No profile yet');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="chef-dashboard">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Chef Dashboard</h1>
          <p className="text-gray-600 mt-2">Welcome back, {user.name}! 👨‍🍳</p>
        </div>
        <NotificationBanner userId={user.id} />
      </div>

      {/* Subscription Notice */}
      <div className="mb-6">
        <ChefSubscription user={user} />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('bookings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'bookings'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="bookings-tab"
          >
            My Bookings ({bookings.length})
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'profile'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="profile-tab"
          >
            Profile & Bio
          </button>
          <button
            onClick={() => setActiveTab('media')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'media'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="media-tab"
          >
            Photos & Videos
          </button>
          
          <button
            onClick={() => setActiveTab('catering')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'catering'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🍽️ Catering
          </button>
          
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Settings
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'bookings' && (
        <div className="space-y-6" data-testid="bookings-section">
          {bookings.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No bookings yet. They&apos;ll appear here once customers book you!</p>
            </div>
          ) : (
            bookings.map((booking) => (
              <div key={booking.id} className="bg-white rounded-lg shadow p-6" data-testid={`booking-${booking.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {booking.meal_type.charAt(0).toUpperCase() + booking.meal_type.slice(1)} for {booking.guest_count} guests
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      📅 {new Date(booking.date).toLocaleDateString()} at {new Date(booking.date).toLocaleTimeString()}
                    </p>
                    <p className="text-sm text-gray-600">
                      📍 {booking.service_city}, {booking.service_state} {booking.service_zip}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {booking.status}
                  </span>
                </div>

                {/* Card Info */}
                {booking.issuing_card_id && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium text-green-900">✅ Grocery card issued</p>
                    <p className="text-sm text-green-700 mt-1">
                      Limit: ${(booking.issuing_limit_cents / 100).toFixed(2)}
                    </p>
                  </div>
                )}

                {/* Receipt Upload */}
                {booking.issuing_card_id && (
                  <ReceiptUploadPanel bookingId={booking.id} />
                )}

                {/* Journey Tracking and Communication for Confirmed Bookings */}
                {booking.status === 'confirmed' && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="flex gap-3 mb-4">
                      <button
                        onClick={() => setSelectedBooking(selectedBooking?.id === booking.id ? null : booking)}
                        className={`flex-1 ${selectedBooking?.id === booking.id ? 'bg-orange-600' : 'bg-blue-600'} hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2`}
                      >
                        📍 {selectedBooking?.id === booking.id ? 'Hide' : 'Manage'} Journey
                      </button>
                      <button
                        onClick={() => {
                          setSelectedBooking(booking);
                          setShowVideoCall(true);
                        }}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
                      >
                        📹 Video Call Customer
                      </button>
                    </div>

                    {/* Chef Status Control Panel */}
                    {selectedBooking && selectedBooking.id === booking.id && (
                      <div className="mt-4">
                        <ChefStatusControl bookingId={booking.id} booking={booking} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'profile' && (
        <div data-testid="profile-section">
          <ChefProfileForm user={user} profile={profile} onUpdate={fetchProfile} />
        </div>
      )}

      {activeTab === 'media' && (
        <div data-testid="media-section">
          <ChefMediaGallery user={user} profile={profile} onUpdate={fetchProfile} />
        </div>
      )}

      {activeTab === 'catering' && (
        <div>
          <ChefCateringManagement userId={user.id} />
        </div>
      )}

      {activeTab === 'settings' && (
        <div data-testid="settings-section">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">⚙️ Settings & Profile Management</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">📜 SafeServ Certification</h3>
                
                {profile?.safeserv_cert_number ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-semibold text-green-900">✅ Certificate on File</p>
                        <p className="text-sm text-green-800 mt-1">
                          <strong>Cert Number:</strong> {profile.safeserv_cert_number}
                        </p>
                        {profile.safeserv_expiration_date && (
                          <p className="text-sm text-green-800">
                            <strong>Expires:</strong> {new Date(profile.safeserv_expiration_date).toLocaleDateString()}
                          </p>
                        )}
                        <p className="text-xs text-green-600 mt-2">
                          If you entered the wrong number, delete it and re-upload during your next signup or contact admin.
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          if (!window.confirm('Delete your SafeServ certification? You can contact admin to add a new one.')) return;
                          try {
                            await axios.delete('/chef/safeserv-cert');
                            alert('✅ SafeServ certification deleted. Contact admin to update with correct information.');
                            fetchProfile();
                          } catch (err) {
                            alert(err.response?.data?.detail || 'Failed to delete certification');
                          }
                        }}
                        className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm font-semibold"
                      >
                        🗑️ Delete Cert
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800">⚠️ No SafeServ certification on file. Contact admin to add your certification.</p>
                  </div>
                )}
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Account Settings</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-600 mb-4">Update your account preferences and notification settings</p>
                  <button 
                    onClick={() => alert('Account settings feature coming soon!')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Manage Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Widget for Active Bookings */}
      {selectedBooking && (
        <ChatWidget
          bookingId={selectedBooking.id}
          userId={user.id}
          userRole="chef"
        />
      )}

      {/* Video Call Modal */}
      {showVideoCall && selectedBooking && (
        <VideoCallModal
          bookingId={selectedBooking.id}
          userId={user.id}
          userRole="chef"
          onClose={() => setShowVideoCall(false)}
        />
      )}
    </div>
  );
};

export default ChefDashboard;
